import { useAnimatedScroll } from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/useAnimatedScroll";
import { CategoryBestBrandsBanner } from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/CategoryBestBrandsBanner/CategoryBestBrandsBanner";
import {
  ITabProps,
  PillWithContent,
} from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/homepage-v2-only/__internal__/PillWithContentComponent/PillWithContent";
import { CategoryBestBrandsWithCTA } from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/homepage-v2-only/CategoryBestBrandsBannerV2/__internal__/CategoryBestBrandsWithCTA";
import { useCategoryBrandsBannerQuery } from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/DataFetching/useCategoryBrandsBannerQuery";
import { ICategoryBrandsCarousel } from "@faire/web-api--source/indigofair/data/ICategoryBrandsCarousel";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isRobot } from "@faire/web/common/user-agent/isRobot";
import { LoadingSkeleton } from "@faire/web/slate/LoadingSkeleton";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import React from "react";

import { useIsLoggedOutHomepageV2 } from "../../../useIsLoggedOutHomepageV2";

const ITEMS_PER_ROW_RESPONSIVE: ResponsiveValues<number> = {
  mobileAndAbove: 4,
  desktopAndAbove: 5,
  xLargeAndAbove: 6,
};

export const CategoryBestBrandsWithV2Design: React.FC<{
  loadTimingRef?: React.RefCallback<HTMLElement>;
}> = ({ loadTimingRef }) => {
  const isHomepageRedesignV2 = useIsLoggedOutHomepageV2();
  if (isHomepageRedesignV2) {
    return <CategoryBestBrandsV2 loadTimingRef={loadTimingRef} />;
  }
  return <CategoryBestBrandsBanner loadTimingRef={loadTimingRef} />;
};

const CategoryBestBrandsV2: React.FC<{
  loadTimingRef?: React.RefCallback<HTMLElement>;
}> = ({ loadTimingRef }) => {
  const { categoryBestBrands, isFetching } = useCategoryBrandsBannerQuery();

  const { strictLocalize } = useStrictLocalization();

  const {
    resumeScroll,
    pauseScroll,
    currentIndex,
    setCurrentIndex,
    isVisible,
  } = useAnimatedScroll(categoryBestBrands?.length ?? 0, 5000);

  const itemsPerRow = useResponsiveValue(ITEMS_PER_ROW_RESPONSIVE);

  const selectedCategory = categoryBestBrands?.[currentIndex];

  const slicedSelectedCategory = ICategoryBrandsCarousel.build({
    ...selectedCategory,
    large_brand_tiles: selectedCategory?.large_brand_tiles?.slice(
      0,
      itemsPerRow
    ),
  });

  const tabs: Array<ITabProps> =
    categoryBestBrands?.map(
      ({
        category_name,
        category_path_identifier,
        category_cta_link,
        category_cta_link_text,
      }) => {
        return {
          tabName: category_name,
          pathIdentifier: category_path_identifier,
          ctaLink: category_cta_link,
          ctaLinkText: category_cta_link_text,
        };
      }
    ) ?? [];

  if (isFetching) {
    return <LoadingSkeleton height="600px" width="100%" />;
  }

  if (!categoryBestBrands?.length || !selectedCategory) {
    return null;
  }

  return (
    <PillWithContent
      title={strictLocalize({
        id: "bej9JA",
        defaultMessage: "Featured brands",
        description: {
          text: "Title for the section showing up popular brands",
          img: "https://cdn.faire.com/fastly/d8c0f6a089fdb7d931a364cbc25ccbd4867222072e4c6b97fb89f04305b92fc3.png",
        },
      })}
      tabs={tabs}
      eventTrackingName="category_best_brands_banner"
      loadTimingRef={loadTimingRef}
      pauseScroll={pauseScroll}
      resumeScroll={resumeScroll}
      currentIndex={currentIndex}
      setCurrentIndex={setCurrentIndex}
      isVisible={isVisible}
    >
      <>
        {isRobot() ? (
          <BotCategories categoryBestBrands={categoryBestBrands} />
        ) : (
          <CategoryBestBrandsWithCTA
            selectedCategory={slicedSelectedCategory}
            pauseScroll={pauseScroll}
            resumeScroll={resumeScroll}
          />
        )}
      </>
    </PillWithContent>
  );
};

const BotCategories: React.FC<{
  categoryBestBrands: ICategoryBrandsCarousel[];
}> = ({ categoryBestBrands }) => (
  // we want to have all categories in the DOM for prerendering
  <>
    {categoryBestBrands.map((category, index) => (
      <CategoryBestBrandsWithCTA
        key={index}
        selectedCategory={category}
        className={index ? "invisible h-0" : ""}
      />
    ))}
  </>
);
